import { useEffect, useMemo, useState } from "react";
import {
  getLogisticsRouteDetail,
  listLogisticsRoutes,
} from "@/lib/logistics/client";
import type { LogiStop } from "@/lib/logistics/types";
import type { MfgPlant } from "@/lib/manufacturing/types";
import {
  buildOpsLookupLocationTriad,
  type OpsLookupLocationSlot,
} from "@/lib/opsLookup/opsLookupLocations";
import type { OpsLookupUnitRow } from "@/lib/opsLookup/opsLookupModel";

/**
 * Enrich plant/dealer/customer triad for the selected order using plant
 * metadata + the unit's latest logistics route stops (when present).
 */
export function useOpsLookupLocations(args: {
  unit: OpsLookupUnitRow | null;
  plant: MfgPlant | null;
}) {
  const { unit, plant } = args;
  const [stops, setStops] = useState<LogiStop[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!unit?.id) {
      setStops([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const listed = await listLogisticsRoutes({ unit_id: unit.id });
      if (cancelled) return;
      if (!listed.ok || !listed.data.routes.length) {
        setStops([]);
        setLoading(false);
        return;
      }
      const active =
        listed.data.routes.find((r) => r.route_state !== "cancelled") ??
        listed.data.routes[0];
      const detail = await getLogisticsRouteDetail(active.id);
      if (cancelled) return;
      setStops(detail.ok ? detail.data.stops ?? [] : []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [unit?.id]);

  const triad: OpsLookupLocationSlot[] = useMemo(() => {
    if (!unit) return [];
    return buildOpsLookupLocationTriad({
      unit,
      plantName: plant?.name ?? null,
      plantNotes: plant?.notes ?? null,
      stops,
    });
  }, [unit, plant, stops]);

  return { triad, loading, stops };
}
